import { useState, FormEvent } from "react";
import {
  Popover,
  Text,
  Button,
  useDisclosure,
  Alert,
  Stack,
  HStack,
  ButtonGroup,
} from "@chakra-ui/react";
import { CraAttribute, CraAttributeType } from "@keita/lanepatrol-sdk";
import { AttributeInput } from "../../../../../shared/views/AttributeInput/AttributeInput";
import { useStores } from "../../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../../shared/views/CustomPopover";
import { parseAttributeInputValue } from "../../../../../coding/utils/attribute-value";
import { t } from "i18next";
import { cellWidth } from "./constants";

interface Props {
  attribute: CraAttribute;
  label: string;
}
export const HeaderCell: React.FC<Props> = ({ attribute, label }) => {
  const popoverState = useDisclosure();

  const text = (
    <Text fontWeight={"semibold"} color="primary.600">
      {label}
    </Text>
  );

  return (
    <HStack
      alignItems={"end"}
      textAlign={attribute.type === CraAttributeType.Number ? "right" : "left"}
      onClick={popoverState.onOpen}
      cursor="pointer"
      _hover={{ bg: "primary.50" }}
      p={2}
      width={`${cellWidth}px`}
    >
      {popoverState.open ? (
        <CustomPopover trigger={text} open onClose={popoverState.onClose}>
          <EditRowForm onClose={popoverState.onClose} attribute={attribute} />
        </CustomPopover>
      ) : (
        text
      )}
    </HStack>
  );
};

const EditRowForm = ({
  attribute,
  onClose,
}: {
  attribute: CraAttribute;
  onClose: () => void;
}) => {
  const [newValue, setNewValue] = useState<string | null>(null);
  const { codingStore, craNodeStore } = useStores();
  const selectedRows = craNodeStore.multiSelectedIds;

  const handleSubmit = async (value: string | null) => {
    const parsedValue = parseAttributeInputValue(attribute, value);
    codingStore.codeAttribute(attribute.name, parsedValue);

    onClose();
  };

  return (
    <>
      <Popover.Body fontWeight="normal" onClick={(e) => e.stopPropagation()}>
        {selectedRows.size === 0 ? (
          <Alert.Root status={"warning"}>
            <Alert.Indicator />
            <Alert.Description fontSize="xs">
              No rows selected.
            </Alert.Description>
          </Alert.Root>
        ) : (
          <form
            id="editCell"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();

              handleSubmit(newValue);
            }}
          >
            <Stack>
              <AttributeInput
                attribute={attribute}
                value={newValue}
                setValue={(inputValue) => {
                  setNewValue(inputValue);

                  if (attribute.type === CraAttributeType.Select) {
                    handleSubmit(inputValue);
                  }
                }}
                allowNullValues
              />

              <Alert.Root status={"info"}>
                <Alert.Indicator />
                <Alert.Description fontSize="xs">
                  {t(`map:rows`, { count: selectedRows.size })} selected
                </Alert.Description>
              </Alert.Root>
            </Stack>
          </form>
        )}
      </Popover.Body>

      <Popover.Footer justifyContent={"end"}>
        <ButtonGroup colorPalette="primary" size="sm">
          <Button variant="ghost" disabled={!selectedRows.size} onClick={onClose}>
            Cancel
          </Button>
          {attribute.type !== CraAttributeType.Select && (
            <Button type="submit" form="editCell" disabled={!selectedRows.size}>
              Set
            </Button>
          )}
        </ButtonGroup>
      </Popover.Footer>
    </>
  );
};
